import React, { useCallback, useEffect, useRef, useState } from 'react'

import { AnimatePresence, motion, useAnimation } from 'framer-motion'
import { ModulesDatoModule } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import OneLogo from './OneLogo'
import { TypewriterText } from './TypewriterText'
import { animationConfig, getLogoColorChangeTime, getWidthForStep } from './animationConfig'

type Props = {
  onClose: () => void
  moduleData: ModulesDatoModule[]
}

type Widths = {
  initial: number
  step1: number
  step2: number
  step3: number
}

export default function Animation({ onClose, moduleData: _moduleData }: Props) {
  // Load text2 only
  const text2 = i18n.t('growth.one_waitlist.animation.one_activated')

  // Check if text is ready
  const [isTextsReady, setIsTextsReady] = useState<boolean>(false)
  const [isWidthsCalculated, setIsWidthsCalculated] = useState(false)
  const [isComponentReady, setIsComponentReady] = useState(false)
  const [widths, setWidths] = useState<Widths | null>(null)

  // Animation states
  const toggleControls = useAnimation()
  const ballControls = useAnimation()
  const gradientControls = useAnimation()
  const [overlayActive, setOverlayActive] = useState(false)
  const [textStep, setTextStep] = useState(0)
  const [isLogoWhite, setIsLogoWhite] = useState(false)
  const [isSequenceRunning, setIsSequenceRunning] = useState(false)
  const [autoCloseTimeout, setAutoCloseTimeout] = useState<NodeJS.Timeout | null>(null)
  const hasAnimationRun = useRef(false)

  // Initialize gradient controls to be hidden
  useEffect(() => {
    gradientControls.set({ opacity: 0 })
  }, [gradientControls])

  // Ensure gradient is hidden when component mounts
  useEffect(() => {
    gradientControls.set({ opacity: 0 })
  }, [isComponentReady, gradientControls])

  // Check text readiness
  useEffect(() => {
    const textsReady = text2 && text2.trim() !== ''

    setIsTextsReady(textsReady || false)
  }, [text2])

  // Calculate widths when text is ready
  useEffect(() => {
    if (isTextsReady && !isWidthsCalculated) {
      const calculateWidths = async () => {
        try {
          const config = animationConfig({ text1: '', text2, waitlistText: '', learnMoreText: '' })
          const ballSize = config.toggle.height - 2 * config.toggle.padding

          const calculatedWidths = {
            initial: await getWidthForStep(
              0,
              ballSize,
              config.toggle.padding,
              config.content.texts,
              config
            ),
            step1: await getWidthForStep(
              1,
              ballSize,
              config.toggle.padding,
              config.content.texts,
              config
            ),
            step2: await getWidthForStep(
              2,
              ballSize,
              config.toggle.padding,
              config.content.texts,
              config
            ),
            step3: await getWidthForStep(
              3,
              ballSize,
              config.toggle.padding,
              config.content.texts,
              config
            ),
          }

          setWidths(calculatedWidths)
          setIsWidthsCalculated(true)
        } catch (error) {
          // Set fallback widths if calculation fails
          const config = animationConfig({ text1: '', text2, waitlistText: '', learnMoreText: '' })
          setWidths({
            initial: config.widths.initial,
            step1: config.widths.minStep1Width,
            step2: config.widths.minStep2Width,
            step3: config.widths.minStep3Width,
          })
          setIsWidthsCalculated(true)
        }
      }

      calculateWidths()
    }
  }, [isTextsReady, isWidthsCalculated, text2])

  // Step 5: Mark component as ready when everything is calculated
  useEffect(() => {
    if (isTextsReady && isWidthsCalculated && widths) {
      setIsComponentReady(true)
    }
  }, [isTextsReady, isWidthsCalculated, widths])

  const closeOverlay = useCallback(() => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeout) {
      clearTimeout(autoCloseTimeout)
      setAutoCloseTimeout(null)
    }

    // Calculate config for close animation
    const currentConfig = animationConfig({ text1: '', text2, waitlistText: '', learnMoreText: '' })

    // Close/hide the animation with smooth fade out
    Promise.all([
      toggleControls.start({
        opacity: 0,
        transition: {
          duration: currentConfig?.timings?.finalFadeOutDuration
            ? currentConfig.timings.finalFadeOutDuration / 1000
            : 0,
          ease: currentConfig?.logo.easing,
        },
      }),
      ballControls.start({
        opacity: 0,
        transition: {
          duration: currentConfig?.timings?.finalFadeOutDuration
            ? currentConfig.timings.finalFadeOutDuration / 1000
            : 0,
          ease: currentConfig?.logo.easing,
        },
      }),
    ]).then(() => {
      setOverlayActive(false)
      setTextStep(0)
      setIsLogoWhite(false)
      // Reset gradient immediately
      gradientControls.set({ opacity: 0 })
      // Call the overlay's onClose function
      onClose?.()
    })
  }, [
    autoCloseTimeout,
    text2,
    toggleControls,
    ballControls,
    gradientControls,
    onClose,
    setAutoCloseTimeout,
    setOverlayActive,
    setTextStep,
    setIsLogoWhite,
  ])

  const sequence = useCallback(async () => {
    if (isSequenceRunning || !isComponentReady || !widths || hasAnimationRun.current) return

    hasAnimationRun.current = true
    setIsSequenceRunning(true)

    // Calculate config inside the callback to avoid circular dependency
    const currentConfig = animationConfig({ text1: '', text2, waitlistText: '', learnMoreText: '' })
    const t = currentConfig?.timings
    const b = currentConfig?.logo
    const c = currentConfig?.toggle
    const currentBallSize = currentConfig
      ? currentConfig.toggle.height - 2 * currentConfig.toggle.padding
      : 0

    try {
      // Ensure gradient is hidden at the start
      gradientControls.set({ opacity: 0 })

      // STEP 0: Fade in toggle + ball
      await new Promise((res) => setTimeout(res, t?.initialFadeInDelay))
      await Promise.all([
        toggleControls.start({
          width: widths.initial,
          height: c?.height,
          borderRadius: c?.borderRadius,
          opacity: 1,
          transition: { type: 'spring', ...c?.spring },
        }),
        ballControls.start({
          x: c?.padding,
          rotate: 0,
          opacity: 1,
          transition: { type: 'spring', ...c?.spring },
        }),
      ])

      // STEP 1: Expand toggle + move ball directly to step2 width
      setOverlayActive(true)
      // Show gradient immediately when the overlay is activated
      gradientControls.start({
        opacity: 1,
        transition: { duration: t?.gradientFadeIn, ease: 'easeInOut' },
      })

      // Change logo color using the helper function
      setTimeout(
        () => {
          setIsLogoWhite(true)
        },
        currentConfig ? getLogoColorChangeTime(currentConfig) : 0
      )

      await Promise.all([
        toggleControls.start({
          width: widths.step2,
          transition: { duration: b?.durationStep2, ease: b?.easing },
        }),
        ballControls.start({
          x: widths.step2 - currentBallSize - (c?.padding ? c.padding : 0),
          rotate: b?.rotateStep2,
          transition: { duration: b?.durationStep2, ease: b?.easing },
        }),
      ])

      // Wait for animation to fully complete before showing text
      await new Promise((res) =>
        setTimeout(res, b?.durationStep2 ? b.durationStep2 * 1000 + 100 : 0)
      )
      setTextStep(2)

      // Wait for text display pause
      await new Promise((res) => setTimeout(res, t?.step2Pause))

      // Set auto-close timeout for 4 seconds (total duration will be 7 seconds)
      const timeout = setTimeout(() => {
        closeOverlay()
      }, 2000) // 2 seconds
      setAutoCloseTimeout(timeout)

      // The animation stays in this state until auto-closes
    } catch (error) {
      // Reset state on error
      hasAnimationRun.current = false
      setIsSequenceRunning(false)
    } finally {
      setIsSequenceRunning(false)
    }
  }, [
    isSequenceRunning,
    isComponentReady,
    widths,
    hasAnimationRun,
    text2,
    gradientControls,
    toggleControls,
    ballControls,
    closeOverlay,
    setAutoCloseTimeout,
    setIsSequenceRunning,
    setOverlayActive,
    setIsLogoWhite,
    setTextStep,
  ])

  // Step 6: Start animation when component is ready
  useEffect(() => {
    let isMounted = true

    const runSequence = async () => {
      if (!isMounted || !isComponentReady || !widths) return

      // Add a longer delay to ensure DOM and fonts are fully ready
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Ensure the component is visible before starting animation
      await new Promise((resolve) => requestAnimationFrame(resolve))

      await sequence()
    }

    if (isComponentReady && widths) {
      runSequence()
    }

    return () => {
      isMounted = false
    }
  }, [isComponentReady, widths, sequence])

  // Backup auto-close timeout - ensures overlay closes even if sequence fails
  useEffect(() => {
    if (!isComponentReady) return

    const backupTimeout = setTimeout(() => {
      closeOverlay()
    }, 8000) // 8 seconds backup timeout

    return () => {
      clearTimeout(backupTimeout)
    }
  }, [isComponentReady, closeOverlay])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimeout) {
        clearTimeout(autoCloseTimeout)
      }
    }
  }, [autoCloseTimeout])

  // Get config and ballSize for rendering (only when ready)
  const config = isComponentReady
    ? animationConfig({ text1: '', text2, waitlistText: '', learnMoreText: '' })
    : null
  const ballSize = config ? config.toggle.height - 2 * config.toggle.padding : 0

  // Return null during loading states instead of showing loading UI
  if (!isTextsReady || !isWidthsCalculated || !isComponentReady) {
    return null
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        background: 'transparent',
        margin: 0,
        padding: 0,
        position: 'relative',
      }}
    >
      {/* Wrapper with relative position for the close button */}
      <div style={{ position: 'relative' }}>
        <motion.div
          animate={toggleControls}
          initial={{
            width: widths?.initial,
            height: config?.toggle.height,
            borderRadius: config?.toggle.borderRadius,
            opacity: 0,
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: config?.toggle.borderRadius,
            backgroundColor: '#ffffff',
            boxShadow: `
              -4px -4px 20px 0px rgba(0, 0, 0, 0.04) inset,
              0px 8px 30px 0px rgba(255, 16, 16, 0.2)
            `,
            backdropFilter: 'blur(16px)',
          }}
        >
          {/* Gradient overlay animated */}
          <motion.div
            animate={gradientControls}
            initial={{ opacity: 0 }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
              borderRadius: '100px',
              zIndex: 1,
            }}
          >
            {/* Gradient animated with smooth movement */}
            <motion.div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '100%',
                borderRadius: '100px',
                backgroundImage: config?.content.gradient,
                backgroundSize: '200% 200%',
                backgroundRepeat: 'no-repeat',
              }}
              animate={{
                backgroundPosition: overlayActive
                  ? ['0% 0%', '100% 0%', '100% 100%', '0% 100%', '0% 0%']
                  : '0% 0%',
              }}
              transition={{
                duration: config?.timings.gradientRotationDuration,
                ease: 'linear',
                repeat: overlayActive ? Infinity : 0,
                repeatType: 'loop',
              }}
            />
          </motion.div>

          {/* Ball logo */}
          <motion.div
            animate={ballControls}
            initial={{ x: config?.toggle.padding, rotate: 0, opacity: 0 }}
            style={{
              width: ballSize,
              height: ballSize,
              position: 'absolute',
              left: 0,
              top: config?.toggle.padding,
              zIndex: 2,
            }}
          >
            <OneLogo isWhite={isLogoWhite} />
          </motion.div>

          {/* Text only */}
          <AnimatePresence mode="wait">
            {textStep === 2 && config && (
              <TypewriterText text={config.content.texts[1] || ''} duration={1} config={config} />
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  )
}
